/**
 * 发码倒计时（前端级，**与手机号无关**）
 * -----------------------------------------------------------------------------
 * 服务端的 60s 冷却按**号码**算 —— `pv:cool:{phoneHash}`。这个口径本身没错：
 * 用户把号填错了，改成新号就该立刻能重发，不该罚他等一分钟。
 *
 * 但只按号码算，界面上就同时存在三个「免费重发」出口：
 *   ① 改号 → 换一条全新配额（服务端允许；前端若跟着重置就等于没锁）
 *   ② 关掉改号弹窗再打开 → 组件卸载，state 归零
 *   ③ 刷新页面 → 同上
 * 三者叠起来，「点一下、改个号、再点一下」就是一条连点通道，几分钟就能把
 * 账号配额（20 条/小时）烧完，而每条都真的在扣短信费。
 *
 * 所以这一层刻意**不跟号码走**：发过一次就锁 `SEND_COOLDOWN_SECONDS` 秒，
 * 改号、关弹窗重开、刷新页面都不解锁。
 *
 * ## 三个实现上的选择
 *   - **锚在绝对时间戳上，不是自减计数器。** 计数器一旦在重新挂载时重新初始化，
 *     这层限制就等于没做 —— 而「重新挂载」在表单里是常态（切步骤、切身份类型）。
 *     存 `until`（epoch 毫秒），每次读都现算，晚回来一秒也不会累积漂移。
 *   - **只延长不缩短。** 并发写或服务端给出更短的值时，保留剩余更长的那个：
 *     锁定被意外缩短是不可恢复的错误方向。
 *   - **存储作为入参可注入。** 默认走 `localStorage`（报名页与个人中心两处入口
 *     共用同一把锁，也顺带管住刷新）；测试用内存实现，不必为一个模块给整个
 *     h5 套上 jsdom（`apps/h5` 的测试环境是 node，见 `vite.config.ts`）。
 *
 * 局限（如实说明）：换设备、换浏览器、清缓存即可绕过。这一层是**体验与成本**
 * 的闸，不是安全边界；真正的兜底始终是服务端按号码 + 按账号的两道配额。
 */
import { useEffect, useState } from 'react';

/** 与服务端 `RESEND_COOLDOWN_SECONDS` 对齐；仅在服务端未回传时兜底 */
export const SEND_COOLDOWN_SECONDS = 60;

const STORAGE_KEY = 'h5.phone.sendUntil';
/** 同页两处入口的通信用自定义事件（`storage` 事件只在跨标签页时触发） */
const SYNC_EVENT = 'h5:phone-cooldown';

/** 只需要读写一个字符串 —— 注入内存实现即可在 node 环境测 */
export interface CooldownStorage {
  read(): string | null;
  write(value: string): void;
}

function browserStorage(): CooldownStorage | null {
  try {
    if (typeof window === 'undefined') return null;
    const ls = window.localStorage;
    return {
      read: () => ls.getItem(STORAGE_KEY),
      write: (value) => ls.setItem(STORAGE_KEY, value),
    };
  } catch {
    // Safari 无痕模式访问 localStorage 会抛（版本间行为不一），当作没有本地锁
    return null;
  }
}

/** 读出锁定截止时刻（epoch 毫秒）；无记录 / 记录损坏一律 0 */
function readUntilMs(store: CooldownStorage | null): number {
  if (!store) return 0;
  const raw = store.read();
  if (raw === null) return 0;
  const until = Number(raw);
  return Number.isFinite(until) && until > 0 ? until : 0;
}

/**
 * 还剩几秒。已过期 / 没有记录 / 记录损坏一律返回 0 —— 读失败的方向必须是「放行」，
 * 否则一条脏数据就能把人永久锁在发码按钮外面。
 */
export function readRemainingSeconds(
  now: number = Date.now(),
  store: CooldownStorage | null = browserStorage(),
): number {
  const left = readUntilMs(store) - now;
  return left > 0 ? Math.ceil(left / 1000) : 0;
}

/**
 * 记一次「刚发过」。`seconds` 用服务端回传的 `cooldownSeconds`（口径只有一处），
 * 取不到时才用 `SEND_COOLDOWN_SECONDS`。
 */
export function markSendCooldown(
  seconds: number = SEND_COOLDOWN_SECONDS,
  now: number = Date.now(),
  store: CooldownStorage | null = browserStorage(),
): void {
  if (!store) return;
  const span = Number.isFinite(seconds) && seconds > 0 ? seconds : SEND_COOLDOWN_SECONDS;
  const next = now + span * 1000;
  // 只延长不缩短：已有更长的锁就原样保留（并发写 / 服务端给出更短的值时别把锁改短）
  if (readUntilMs(store) >= next) return;
  try {
    store.write(String(next));
  } catch {
    // 配额写满 / 无痕模式：写不进去就退回「本组件内自己记住」，不阻断发码
    return;
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SYNC_EVENT));
}

/**
 * 组件侧读取。两处入口共用同一把锁：一处在发码，另一处的按钮同时变灰。
 */
export function useSendCooldown(): {
  remaining: number;
  markSent: (seconds?: number) => void;
} {
  const [remaining, setRemaining] = useState(readRemainingSeconds);

  // 每秒对一次表。`remaining` 进依赖是刻意的：归零后这条定时链自己停下，
  // 而不是留一个永远在跑的 interval。
  useEffect(() => {
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setRemaining(readRemainingSeconds()), 1000);
    return () => window.clearTimeout(timer);
  }, [remaining]);

  // 另一处入口发码后立刻同步（否则切回本页还要等下一次 tick 才变灰）
  useEffect(() => {
    const sync = () => setRemaining(readRemainingSeconds());
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return { remaining, markSent: markSendCooldown };
}
